/**
 * Campaigns UI reference config (SCRUM-205), ported verbatim from Ryan Lynch's
 * prototype. These constants are the single source of truth for the four insight
 * types, holdings glyphs, and ledger filter chips used across the ledger, drawer,
 * insights row, and wizard.
 */
import type { InsightType } from '../intelligence-dashboard/insight.types';

// ── Insight types ───────────────────────────────────────────────────────────
/** Render/sort order for the four signal dots (Ryan's INSIGHT_ORDER). */
export const INSIGHT_ORDER: readonly InsightType[] = ['dormant', 'flight', 'smb', 'relgap'];

export interface InsightMeta {
  /** Long label (drawer signal section, tooltips). */
  label: string;
  /** Short label (ledger, chips). */
  short: string;
  /** CSS custom-property name for this insight's semantic color (see tokens.css). */
  colorVar: string;
}

/**
 * Insight type → display metadata. Shared map (audit RT5/6): the Intelligence
 * Dashboard's InsightPriorityList uses the same four types; importing this map
 * keeps the type list in one place instead of re-declaring the literal tuple.
 */
export const INSIGHT_TYPES: Record<InsightType, InsightMeta> = {
  dormant: { label: 'Dormant Account', short: 'Dormant', colorVar: '--insight-dormant' },
  flight: { label: 'Deposit Flight Risk', short: 'Flight Risk', colorVar: '--insight-flight' },
  smb: { label: 'SMB Growth', short: 'SMB Growth', colorVar: '--insight-smb' },
  relgap: { label: 'Relationship Gap', short: 'Rel. Gap', colorVar: '--insight-relgap' },
};

// ── Holdings ────────────────────────────────────────────────────────────────
/** Chip render order (Ryan's HOLDINGS_ORDER). */
export const HOLDINGS_ORDER: readonly string[] = [
  'CHK',
  'SAV',
  'CD',
  'LN',
  'CC',
  'MM',
  'HE',
  'BIZ',
];

/** Holding code → full label (chip tooltip + portfolio section). */
export const HOLDINGS_LABELS: Record<string, string> = {
  CHK: 'Checking',
  SAV: 'Savings',
  CD: 'CD',
  LN: 'Loan',
  CC: 'Credit Card',
  MM: 'Money Market',
  HE: 'HELOC',
  BIZ: 'Business Account',
};

// ── Pagination ──────────────────────────────────────────────────────────────
export const PAGE_SIZE = 25;
